import { useEffect, useRef } from 'react';

type Condition = {
  field: string;
  operator: string;
  value: string | number | object | null;
};

export type Output = {
  field: string;
  value: string;
  fieldError?: string;
  valueError?: string;
};

export const useAutomaticallyFocusOnFirstEmptyConditionOrOutput = ({
  conditions,
  outputs,
  disableOnMount,
}: {
  conditions: Condition[];
  outputs: Output[];
  disableOnMount: boolean;
}) => {
  const conditionFieldName = 'condition-field';
  const conditionOperatorName = 'condition-operator';
  const conditionValueName = 'condition-value';

  const conditionsReference = useRef(conditions);

  useEffect(() => {
    if (
      (conditionsReference.current.length === 0 &&
        conditions.length === 1 &&
        conditions[0].field === '') ||
      disableOnMount
    ) {
      return;
    }
    const changedCondition = conditions.find(
      (condition, index) => condition !== conditionsReference.current[index],
    );

    if (document.activeElement === document.body || changedCondition) {
      const getConditionValue = (element: Element) => {
        return conditions.find((condition) =>
          element.getAttribute('name')?.endsWith(condition.field),
        );
      };

      const valueElements = Array.from(
        document.querySelectorAll(`[name^=${conditionValueName}]`),
      ).filter((element) => {
        const value = getConditionValue(element)?.value;
        return !(value && typeof value === 'object'
          ? Object.values(value).every(Boolean)
          : value);
      });

      const operatorElements = Array.from(
        document.querySelectorAll(`[name^=${conditionOperatorName}][value=""]`),
      );

      const fieldElements = Array.from(
        document.querySelectorAll(`[name^=${conditionFieldName}][value=""]`),
      );

      let focusedElement: Element | null = null;
      [...fieldElements, ...operatorElements, ...valueElements].forEach(
        (element) => {
          if (element instanceof HTMLInputElement) {
            if (
              !focusedElement &&
              (!changedCondition ||
                getConditionValue(element) === changedCondition)
            ) {
              element.focus();
              element.click();
              focusedElement = element;
            } else {
              element.blur();
            }
          }
        },
      );
    }

    conditionsReference.current = conditions;
  }, [conditions]);

  const outputFieldName = 'output-field';
  const outputValueName = 'output-value';

  const outputsReference = useRef(outputs);

  useEffect(() => {
    if (
      (outputsReference.current.length === 0 &&
        outputs.length === 1 &&
        outputs[0].field === '') ||
      disableOnMount
    ) {
      return;
    }
    const changedOutput = outputs.find(
      (output, index) => output !== outputsReference.current[index],
    );

    if (changedOutput) {
      const getOutputValue = (element: Element) => {
        return outputs.find((output) =>
          element.getAttribute('name')?.endsWith(output.field),
        );
      };

      const valueElements = Array.from(
        document.querySelectorAll(`[name^=${outputValueName}]`),
      ).filter((element) => {
        const value = getOutputValue(element)?.value;
        return !(value && typeof value === 'object'
          ? Object.values(value).every(Boolean)
          : value);
      });

      const fieldElements = Array.from(
        document.querySelectorAll(`[name^=${outputFieldName}][value=""]`),
      );

      let focusedElement: Element | null = null;
      [...fieldElements, ...valueElements].forEach((element) => {
        if (element instanceof HTMLInputElement) {
          if (!focusedElement && getOutputValue(element) === changedOutput) {
            element.focus();
            element.click();
            focusedElement = element;
          } else {
            element.blur();
          }
        }
      });
    }

    outputsReference.current = outputs;
  }, [outputs]);

  return {
    conditionFieldName,
    conditionOperatorName,
    conditionValueName,
    outputFieldName,
    outputValueName,
  };
};
